import { ActionInfo } from '@models/game-config.interface';
import Phaser from 'phaser';
import { NodeVisualState } from '@enums/node-states.enum';
import { NodeView } from '@models/nodes';
import {
  CHECKPOINT_NODE_WIDTH,
  CHECKPOINT_NODE_HEIGHT,
} from '@constants/common.const';
import {
  createCelebrationSprite,
  playAnimation,
} from '../../../ui/animations/CelebrationFx';
import { BaseNodeStrategy } from './BaseNodeStrategy';
import { StateChangeHelpers, NodeEventContext } from './NodeVisualStrategy';

/**
 * Strategy for Checkpoint node type.
 * Uses larger size (128x128), custom texture, no outline, plays celebration on reach.
 */
export class CheckpointNodeStrategy extends BaseNodeStrategy {
  // Cache FX sprites per scene to avoid recreating them
  private fxCache = new WeakMap<Phaser.Scene, Phaser.GameObjects.Sprite>();

  getSize(): { width: number; height: number } {
    return { width: CHECKPOINT_NODE_WIDTH, height: CHECKPOINT_NODE_HEIGHT };
  }

  getDefaultNodeImage(
    scene: Phaser.Scene,
    action: ActionInfo,
  ): {
    sprite: Phaser.GameObjects.Image;
    scaleX: number;
    scaleY: number;
  } {
    const textureKey = action.uuid;
    const { width, height } = this.getSize();
    const tex = scene.textures
      .get(textureKey)
      .getSourceImage() as HTMLImageElement;
    const scaleX = width / tex.width;
    const scaleY = height / tex.height;
    const sprite = scene.add.image(0, 0, textureKey);
    sprite.setOrigin(0.5).setScale(scaleX, scaleY);
    // checkpoint image should be black and white on start
    const fx = sprite.preFX?.addColorMatrix();
    if (fx) {
      fx.blackWhite(true);
    }
    return {
      sprite,
      scaleX,
      scaleY,
    };
  }

  getTextureForState(_state: NodeVisualState, _scene: Phaser.Scene): void {
    return;
  }

  supportsOutline(): boolean {
    return false;
  }

  onStateChange(
    view: NodeView,
    state: NodeVisualState,
    _helpers: StateChangeHelpers,
  ): void {
    if (state === NodeVisualState.Active || state === NodeVisualState.Passed) {
      view.sprite.preFX?.clear();
    }
  }

  onPassThrough(context: NodeEventContext, view: NodeView): void {
    this.playAnimation(context, view);
  }

  onLanded(context: NodeEventContext, view: NodeView): void {
    this.playAnimation(context, view);
  }

  private playAnimation(context: NodeEventContext, view: NodeView): void {
    const { scene, fxLayer } = context;
    // remove black and white filter
    view.sprite.preFX?.clear();

    // Lazily create and cache the FX sprite
    let fx = this.fxCache.get(scene);
    if (fx && !fx.active) {
      this.fxCache.delete(scene);
      fx = undefined;
    }
    if (!fx) {
      fx =
        createCelebrationSprite(scene, {
          x: 0,
          y: 0,
          depth: 0,
          scrollFactor: 1,
        }) ?? undefined;

      if (fx) {
        fx.setVisible(false);
        fxLayer.add(fx);
        this.fxCache.set(scene, fx);
      }
    }

    if (!fx) return;

    // Place the FX at the checkpoint node's top-center
    const bounds = view.sprite.getBounds();
    const parent = view.sprite.parentContainer;
    const localX = parent ? bounds.centerX - parent.x : bounds.centerX;
    const localY = parent ? bounds.top - parent.y : bounds.top;

    fx.setPosition(localX, localY);
    fx.setVisible(true);

    // Restart if it was already playing
    fx.anims?.stop();

    playAnimation(scene, fx, {
      repeat: 1,
      timeScale: 2,
    });

    fx.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      if (!fx.active) return;
      fx.setVisible(false);
    });
  }
}
